<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="relative z-50" @close="close">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:max-w-3xl sm:p-6">
                            <div class="flex items-start justify-between gap-4">
                                <div>
                                    <DialogTitle as="h3" class="text-lg font-semibold leading-6 text-gray-900">Property photos</DialogTitle>
                                    <p class="mt-1 text-sm text-gray-500">
                                        {{ orderedPhotos.length }} of {{ maxPhotos }} photos. The first photo is the cover shown on your listing.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    class="rounded-md p-1 text-gray-400 hover:text-gray-600"
                                    aria-label="Close"
                                    @click="close"
                                >
                                    <font-awesome-icon :icon="faXmark" class="h-5 w-5" aria-hidden="true" />
                                </button>
                            </div>

                            <p v-if="serverError" class="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                                {{ serverError }}
                            </p>

                            <div v-if="orderedPhotos.length" class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                                <div
                                    v-for="(photo, index) in orderedPhotos"
                                    :key="photo.id"
                                    class="overflow-hidden rounded-lg border border-gray-200 bg-white"
                                    data-test="photo-tile"
                                >
                                    <div class="relative aspect-[4/3] bg-gray-100">
                                        <img
                                            v-if="photo.url"
                                            :src="photo.url"
                                            :alt="`Property photo ${index + 1}`"
                                            class="h-full w-full object-cover"
                                        />
                                        <div v-else class="flex h-full items-center justify-center px-2 text-center text-xs text-gray-500">
                                            Preview unavailable
                                        </div>
                                        <span
                                            v-if="index === 0"
                                            class="absolute left-2 top-2 rounded-full bg-rent-risk-blue-400 px-2 py-0.5 text-xs font-semibold text-white"
                                        >
                                            Cover
                                        </span>
                                    </div>
                                    <div class="flex items-center justify-between gap-1 px-2 py-1.5">
                                        <div class="flex items-center gap-1">
                                            <button
                                                type="button"
                                                class="rounded p-1.5 text-gray-600 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30"
                                                :aria-label="`Move photo ${index + 1} earlier`"
                                                :disabled="index === 0 || busy"
                                                @click="move(index, -1)"
                                            >
                                                <font-awesome-icon :icon="faArrowLeft" class="h-3.5 w-3.5" aria-hidden="true" />
                                            </button>
                                            <button
                                                type="button"
                                                class="rounded p-1.5 text-gray-600 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-30"
                                                :aria-label="`Move photo ${index + 1} later`"
                                                :disabled="index === orderedPhotos.length - 1 || busy"
                                                @click="move(index, 1)"
                                            >
                                                <font-awesome-icon :icon="faArrowRight" class="h-3.5 w-3.5" aria-hidden="true" />
                                            </button>
                                        </div>
                                        <div v-if="confirmingRemovalId === photo.id" class="flex items-center gap-1">
                                            <button
                                                type="button"
                                                class="rounded px-2 py-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                                :disabled="busy"
                                                @click="remove(photo)"
                                            >
                                                Remove
                                            </button>
                                            <button
                                                type="button"
                                                class="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                                                @click="confirmingRemovalId = null"
                                            >
                                                Keep
                                            </button>
                                        </div>
                                        <button
                                            v-else
                                            type="button"
                                            class="rounded p-1.5 text-gray-600 hover:bg-red-50 hover:text-red-600 disabled:pointer-events-none disabled:opacity-30"
                                            :aria-label="`Remove photo ${index + 1}`"
                                            :disabled="busy || orderChanged"
                                            @click="confirmingRemovalId = photo.id"
                                        >
                                            <font-awesome-icon :icon="faTrashCan" class="h-3.5 w-3.5" aria-hidden="true" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <p v-else class="mt-4 rounded-lg bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
                                No photos yet. Your listing shows a placeholder image until you add one.
                            </p>

                            <div v-if="orderChanged" class="mt-3 flex flex-col gap-2 rounded-md bg-amber-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                                <p class="text-sm text-amber-800">You changed the photo order. Save it before adding or removing photos.</p>
                                <div class="flex gap-2">
                                    <button
                                        type="button"
                                        class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                        :disabled="busy"
                                        @click="resetOrder"
                                    >
                                        Undo
                                    </button>
                                    <button
                                        type="button"
                                        class="rounded-md bg-rent-risk-blue-400 px-3 py-1.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
                                        :disabled="busy"
                                        @click="saveOrder"
                                    >
                                        {{ savingOrder ? 'Saving…' : 'Save order' }}
                                    </button>
                                </div>
                            </div>

                            <div class="mt-5 border-t border-gray-200 pt-4">
                                <h4 class="text-sm font-semibold text-gray-900">Add photos</h4>
                                <p v-if="slotsForNewPhotos <= 0" class="mt-1 text-sm text-gray-500" data-test="limit-reached">
                                    This property has reached the {{ maxPhotos }}-photo limit. Remove a photo to add another.
                                </p>
                                <template v-else>
                                    <input
                                        ref="fileInput"
                                        type="file"
                                        multiple
                                        accept="image/jpeg,image/jpg,image/png,image/webp"
                                        class="hidden"
                                        @change="onFileChange"
                                    />
                                    <div
                                        role="button"
                                        tabindex="0"
                                        :class="[
                                            'mt-2 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-3 py-5 transition',
                                            dragActive ? 'border-rent-risk-blue-400 bg-rent-risk-blue-100' : 'border-gray-300 bg-gray-50/90 hover:border-gray-400',
                                            uploadDisabled ? 'pointer-events-none opacity-50' : '',
                                        ]"
                                        @click="openFilePicker"
                                        @keydown.enter="openFilePicker"
                                        @keydown.space.prevent="openFilePicker"
                                        @dragover.prevent="dragActive = true"
                                        @dragleave="onDragLeave"
                                        @drop.prevent="onDrop"
                                    >
                                        <font-awesome-icon :icon="faCloudArrowUp" class="h-5 w-5 text-rent-risk-blue-400" aria-hidden="true" />
                                        <p class="mt-1 text-center text-sm font-medium text-gray-800">
                                            Drop images or <span class="text-rent-risk-blue-400 underline underline-offset-2">browse</span>
                                        </p>
                                        <p class="mt-1 text-center text-xs text-gray-500">
                                            JPG, PNG, or WebP, up to 5&nbsp;MB each. You can add {{ remainingSlots }} more.
                                        </p>
                                    </div>

                                    <p v-if="clientError" class="mt-2 text-sm text-red-600">{{ clientError }}</p>

                                    <ul v-if="pendingFiles.length" class="mt-3 flex flex-wrap gap-2">
                                        <li
                                            v-for="(pending, index) in pendingFiles"
                                            :key="pending.previewUrl"
                                            class="relative h-20 w-24 overflow-hidden rounded-md ring-1 ring-gray-200"
                                        >
                                            <img :src="pending.previewUrl" :alt="pending.file.name" class="h-full w-full object-cover" />
                                            <button
                                                type="button"
                                                class="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-gray-700 shadow hover:bg-white"
                                                :aria-label="`Don't upload ${pending.file.name}`"
                                                :disabled="uploadForm.processing"
                                                @click="removePending(index)"
                                            >
                                                <font-awesome-icon :icon="faXmark" class="h-3 w-3" aria-hidden="true" />
                                            </button>
                                        </li>
                                    </ul>

                                    <div v-if="uploadForm.processing && uploadForm.progress" class="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                                        <div class="h-full bg-rent-risk-blue-400" :style="{ width: `${uploadForm.progress.percentage}%` }" />
                                    </div>

                                    <div class="mt-3 flex justify-end">
                                        <button
                                            type="button"
                                            class="rounded-lg bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                                            :disabled="!pendingFiles.length || uploadDisabled"
                                            @click="upload"
                                        >
                                            <span v-if="uploadForm.processing">Uploading…</span>
                                            <span v-else>Upload {{ pendingFiles.length || '' }} {{ pendingFiles.length === 1 ? 'photo' : 'photos' }}</span>
                                        </button>
                                    </div>
                                </template>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { router, useForm } from '@inertiajs/vue3'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowLeft, faArrowRight, faCloudArrowUp, faTrashCan, faXmark } from '@fortawesome/pro-solid-svg-icons'

const route =
    (typeof window !== 'undefined' && window.route) ||
    ((..._args) => '#')

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    property: {
        type: Object,
        required: true,
    },
})

const emit = defineEmits(['close'])

const maxPhotos = 15
const maxBytes = 5 * 1024 * 1024
const reloadProps = ['property', 'flash']

const orderedPhotos = ref([])
const pendingFiles = ref([])
const confirmingRemovalId = ref(null)
const clientError = ref('')
const serverError = ref('')
const dragActive = ref(false)
const savingOrder = ref(false)
const removing = ref(false)
const fileInput = ref(null)

const uploadForm = useForm({ photos: [] })

const savedPhotoIds = computed(() => (props.property.photos || []).map((photo) => photo.id))
const orderChanged = computed(() => orderedPhotos.value.map((photo) => photo.id).join(',') !== savedPhotoIds.value.join(','))
const busy = computed(() => uploadForm.processing || savingOrder.value || removing.value)
const uploadDisabled = computed(() => busy.value || orderChanged.value)
const slotsForNewPhotos = computed(() => maxPhotos - orderedPhotos.value.length)
const remainingSlots = computed(() => Math.max(0, slotsForNewPhotos.value - pendingFiles.value.length))

watch(
    () => props.property.photos,
    (photos) => {
        orderedPhotos.value = [...(photos || [])]
        confirmingRemovalId.value = null
    },
    { immediate: true },
)

const firstError = (errors) => {
    const key = Object.keys(errors || {}).find((name) => name === 'photos' || name.startsWith('photos.') || name.startsWith('photo_ids'))

    return key ? errors[key] : ''
}

const isAcceptableFile = (file) => {
    const nameOk = /\.(jpe?g|png|webp)$/i.test(file.name)
    const type = (file.type || '').toLowerCase()

    return ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(type) || (type === '' && nameOk)
}

const addFiles = (files) => {
    clientError.value = ''
    serverError.value = ''

    for (const file of files) {
        if (remainingSlots.value <= 0) {
            clientError.value = `A property can have up to ${maxPhotos} photos. Some files were not added.`
            break
        }
        if (!isAcceptableFile(file)) {
            clientError.value = `${file.name} is not a JPG, PNG, or WebP file.`
            continue
        }
        if (file.size > maxBytes) {
            clientError.value = `${file.name} is over 5 MB. Please choose a smaller image.`
            continue
        }
        pendingFiles.value.push({ file, previewUrl: URL.createObjectURL(file) })
    }
}

const clearPending = () => {
    pendingFiles.value.forEach((pending) => URL.revokeObjectURL(pending.previewUrl))
    pendingFiles.value = []
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

const removePending = (index) => {
    const [pending] = pendingFiles.value.splice(index, 1)
    if (pending) {
        URL.revokeObjectURL(pending.previewUrl)
    }
}

const openFilePicker = () => {
    if (!uploadDisabled.value) {
        fileInput.value?.click()
    }
}

const onFileChange = (event) => {
    addFiles(Array.from(event.target?.files || []))
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

const onDragLeave = (event) => {
    const el = event.currentTarget
    if (el && event.relatedTarget instanceof Node && el.contains(event.relatedTarget)) {
        return
    }
    dragActive.value = false
}

const onDrop = (event) => {
    dragActive.value = false
    addFiles(Array.from(event.dataTransfer?.files || []))
}

const upload = () => {
    if (!pendingFiles.value.length) {
        return
    }
    serverError.value = ''
    uploadForm.photos = pendingFiles.value.map((pending) => pending.file)
    uploadForm.post(route('properties.photos.store', props.property.id), {
        forceFormData: true,
        preserveScroll: true,
        only: reloadProps,
        onSuccess: () => {
            clearPending()
            uploadForm.reset()
        },
        onError: (errors) => {
            serverError.value = firstError(errors) || 'Could not upload the photos. Please try again.'
        },
    })
}

const move = (index, direction) => {
    const target = index + direction
    if (target < 0 || target >= orderedPhotos.value.length) {
        return
    }
    const photos = [...orderedPhotos.value]
    ;[photos[index], photos[target]] = [photos[target], photos[index]]
    orderedPhotos.value = photos
    confirmingRemovalId.value = null
}

const resetOrder = () => {
    orderedPhotos.value = [...(props.property.photos || [])]
}

const saveOrder = () => {
    serverError.value = ''
    router.put(
        route('properties.photos.reorder', props.property.id),
        { photo_ids: orderedPhotos.value.map((photo) => photo.id) },
        {
            preserveScroll: true,
            only: reloadProps,
            onStart: () => { savingOrder.value = true },
            onFinish: () => { savingOrder.value = false },
            onError: (errors) => {
                serverError.value = firstError(errors) || 'Could not save the photo order. Please try again.'
            },
        },
    )
}

const remove = (photo) => {
    serverError.value = ''
    router.delete(route('properties.photos.destroy', [props.property.id, photo.id]), {
        preserveScroll: true,
        only: reloadProps,
        onStart: () => { removing.value = true },
        onFinish: () => {
            removing.value = false
            confirmingRemovalId.value = null
        },
        onError: () => {
            serverError.value = 'Could not remove the photo. Please try again.'
        },
    })
}

const close = () => {
    if (busy.value) {
        return
    }
    clearPending()
    resetOrder()
    clientError.value = ''
    serverError.value = ''
    confirmingRemovalId.value = null
    uploadForm.clearErrors()
    emit('close')
}

onBeforeUnmount(clearPending)
</script>
